import React from 'react';
import {
  mutedTextRoleStyle,
  scaledTextRoleStyle,
  type TextRoleStyle,
} from './semanticTextRoles';

/**
 * WEB-926, позиция 7 — сравнение «сейчас / цель» через бары (референс BetterMe:
 * карточка с двумя колонками стат, до/после ДАННЫМИ, а не фото; колонка «сейчас»
 * приглушена, колонка «цель» акцентная).
 *
 * ИСТОЧНИК ДАННЫХ — АВТОР, и это не упрощение. «Цели» в ответах пользователя
 * нет вообще (её никто не спрашивал), а «сейчас» лежит в ответах голым числом
 * без единицы и без шкалы — превратить его в длину бара нечем. Автор пишет
 * значения строками (и может вставить в них {{переменную}} со своим ответом),
 * длину бара задаёт процентом.
 *
 * Раскладка: подпись показателя — одной строкой на всю ширину, под ней два
 * бара рядом. Так подпись не дублируется в обеих колонках и разница читается
 * шириной бара, а не только цветом (важно для монохрома и дальтонизма).
 */

const MIN_PERCENT = 0;
const MAX_PERCENT = 100;

export interface NowGoalBarsRow {
  id: string;
  label: string;
  nowText?: string;
  nowPercent?: number;
  goalText?: string;
  goalPercent?: number;
}

/** Проценты приходят от автора и из генерации — за карточку выйти не должны. */
function clampPercent(value: number | undefined): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return MIN_PERCENT;
  return Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, Math.round(value)));
}

/**
 * WEB-1692 юнит 4: отношение кегля НАЗВАНИЯ СТРОКИ к кеглю подписей полос —
 * ровно то, что стоит в вёрстке (0.75em против 0.875em). Вариант А: автор
 * задаёт одно число, пропорция считается сама.
 */
const ROW_LABEL_RATIO = 0.75 / 0.875;

interface BarCellProps {
  testId: string;
  percent: number;
  color: string;
  text?: string;
  muted: boolean;
  /** WEB-1692 юнит 4: накладка роли подписей полос. Пусто = `{}`. */
  labelRole?: TextRoleStyle;
}

function BarCell({
  testId,
  percent,
  color,
  text,
  muted,
  labelRole = {},
}: BarCellProps): React.ReactElement {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          height: 8,
          borderRadius: 999,
          background: 'var(--color-bg-tertiary, #f0f0f0)',
          overflow: 'hidden',
        }}
      >
        <div
          data-testid={testId}
          data-percent={String(percent)}
          style={{ width: `${percent}%`, height: '100%', background: color, borderRadius: 999 }}
        />
      </div>
      {text && (
        <div
          data-testid={`${testId}-text`}
          style={{
            marginTop: 6,
            fontSize: '0.875em',
            fontWeight: 600,
            // WEB-1440 (класс WEB-966): подписи лежат на СВОЕЙ карточке блока.
            color: muted
              ? 'var(--color-text-on-surface, var(--color-text-secondary, #595959))'
              : 'var(--color-text-on-surface, var(--color-text-primary, rgba(0,0,0,0.88)))',
            // WEB-1692 юнит 4, вариант А: авторский цвет ставится ЦЕЛИКОМ, а
            // сторона «сейчас» приглушается прозрачностью от него же —
            // разница, ради которой блок так и нарисован, не теряется. Без
            // авторского цвета приглушение не появляется вовсе.
            ...labelRole,
            ...(muted ? mutedTextRoleStyle(labelRole) : {}),
          }}
        >
          {text}
        </div>
      )}
    </div>
  );
}

interface NowGoalBarsBlockProps {
  title?: React.ReactNode;
  nowLabel: string;
  goalLabel: string;
  rows: NowGoalBarsRow[];
  note?: string;
  accentColor?: string;
  /** WEB-1692 юнит 4: заголовок блока. Пусто = `{}`. */
  titleRole?: TextRoleStyle;
  /**
   * WEB-1692 юнит 4: подписи полос — шапки колонок, названия показателей и
   * значения под полосами. Кегль автора ставится на шапки и значения, название
   * показателя считается от него прежним отношением (вариант А).
   */
  labelsRole?: TextRoleStyle;
}

export function NowGoalBarsBlock({
  title,
  nowLabel,
  goalLabel,
  rows,
  note,
  accentColor,
  titleRole = {},
  labelsRole = {},
}: NowGoalBarsBlockProps): React.ReactElement | null {
  // Пустая карточка на публичной странице читается как сломанный экран —
  // лучше не рисовать ничего (тот же принцип, что у остальных списочных блоков).
  if (rows.length === 0) return null;

  const accent = accentColor || 'var(--color-primary, #4094F7)';
  // on-surface не нужен: это заливка САМОГО БАРА (приглушённая колонка
  // «сейчас»), а не текст — авто-контраст сделал бы её жирным акцентом.
  const nowColor = 'var(--color-text-tertiary, #bfbfbf)';

  return (
    <div data-testid="now-goal-bars" style={{ marginBottom: 16 }}>
      {title && (
        <div
          data-testid="now-goal-bars-title"
          style={{
            fontSize: '1.125em',
            fontWeight: 600,
            marginBottom: 12,
            textAlign: 'center',
            ...titleRole,
          }}
        >
          {title}
        </div>
      )}
      <div
        style={{
          padding: 16,
          borderRadius: 'var(--radius-card, var(--border-radius-lg, 12px))',
          border: '1px solid var(--color-border-primary, #e8e8e8)',
          background: 'var(--color-bg-surface, #fff)',
          boxShadow: 'var(--card-shadow, none)',
        }}
      >
        <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
          <div
            data-testid="now-goal-bars-now-header"
            style={{
              flex: 1,
              fontSize: '0.875em',
              fontWeight: 500,
              color: 'var(--color-text-on-surface, var(--color-text-tertiary, #8c8c8c))',
              // Шапка «сейчас» — та же приглушённая сторона, что и подпись
              // полосы: вариант А красит её авторским цветом с прозрачностью.
              ...labelsRole,
              ...mutedTextRoleStyle(labelsRole),
            }}
          >
            {nowLabel}
          </div>
          <div
            data-testid="now-goal-bars-goal-header"
            style={{
              flex: 1,
              fontSize: '0.875em',
              fontWeight: 700,
              color: 'var(--color-text-on-surface, var(--color-text-primary, rgba(0,0,0,0.88)))',
              ...labelsRole,
            }}
          >
            {goalLabel}
          </div>
        </div>
        {rows.map((row, i) => (
          <div key={row.id} data-testid={`now-goal-row-${i}`} style={{ marginBottom: 14 }}>
            <div
              data-testid={`now-goal-row-${i}-label`}
              style={{
                fontSize: '0.75em',
                marginBottom: 6,
                color: 'var(--color-text-on-surface, var(--color-text-secondary, #595959))',
                // Название показателя мельче значений — отношение сохраняется
                // от авторского кегля (вариант А), а не схлопывается в один.
                ...scaledTextRoleStyle(labelsRole, ROW_LABEL_RATIO),
              }}
            >
              {row.label}
            </div>
            <div style={{ display: 'flex', gap: 16 }}>
              <BarCell
                testId={`now-goal-row-${i}-now-bar`}
                percent={clampPercent(row.nowPercent)}
                color={nowColor}
                text={row.nowText}
                muted
                labelRole={labelsRole}
              />
              <BarCell
                testId={`now-goal-row-${i}-goal-bar`}
                percent={clampPercent(row.goalPercent)}
                color={accent}
                text={row.goalText}
                muted={false}
                labelRole={labelsRole}
              />
            </div>
          </div>
        ))}
      </div>
      {note && (
        <div
          data-testid="now-goal-bars-note"
          style={{
            marginTop: 10,
            textAlign: 'center',
            fontSize: '0.75em',
            // on-surface не нужен: сноска лежит на фоне ЭКРАНА, не на карточке.
            color: 'var(--color-text-tertiary, #8c8c8c)',
          }}
        >
          {note}
        </div>
      )}
    </div>
  );
}
